import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'

import { t, te, locale } from '@/plugins/i18n'
import { setUser, setTag, setTags } from '@/services/sentry'
import { updateSW } from '@/main'

import authenticateService from '@/services/api/authenticate'
import gamesService from '@/services/api/games'
import userService from '@/services/api/user'
import childService from '@/services/api/child'
import gamesScheduleService from '@/services/api/games_schedule'
import faqsService from '@/services/api/faqs'
import recommendedSessionsService from '@/services/api/recommended_sessions'

import { sendEvent, setUserID, setUserProps } from '@/services/analytics'
import {
  signInWithGoogle,
  signInWithApple,
  signUpWithEmail,
  signInWithEmail,
  signInWithCustomTokenInternal
} from '@/services/authentication'
import router from '@/router'
import user from '@/services/api/user'

const INIT_STORE = 'initStore'
const USER_STORE = 'userStore'
const LOGIN_STORE = 'loginStore'
const GAMES_STORE = 'gamesStore'
const CATEGORIES_STORE = 'categoriesStore'
const SUBCATEGORIES_STORE = 'subcategoriesStore'
const OBJECTIVES_STORE = 'objectivesStore'

export const useAppGlobalStore = defineStore('app_global', {
  state: () => ({
    snackbar: false as boolean,
    snackbarMessage: '' as string,
    snackbarEnableAccept: false as boolean,
    timeout: 4000,
    popupGlobalMessage: '' as string,
    popupGlobalEnable: false as boolean,
    popupExpireTestShowed: false as boolean,
    hasPlayedAGame: false as boolean,
    history: [] as Array<any>,
    redirectTo: '' as string
  }),
  actions: {
    setSnackbarText(message: string, enableAccept: boolean = false, timeout: number = 4000) {
      this.snackbarMessage = message
      this.snackbar = true
      this.snackbarEnableAccept = enableAccept
      this.timeout = timeout
    },
    closeSnackbar() {
      this.snackbar = false
    },
    acceptSnackbar() {
      this.snackbar = false
      updateSW()
    },
    setGlobalPopupText(message: string, enable: boolean = false) {
      this.popupGlobalMessage = message
      this.popupGlobalEnable = enable
    },
    setGlobalPopupEnable(enable: boolean) {
      this.popupGlobalEnable = enable
    },
    setHasPlayedAGame() {
      this.hasPlayedAGame = true
    },
    popupExpireTestShown() {
      this.popupExpireTestShowed = true
    },
    resetPopupExpireTest() {
      this.popupExpireTestShowed = false
    },
    addPageToHistory(page: any) {
      this.history.push(page)
    },
    setRedirect(to: string) {
      this.redirectTo = to
    }
  }
})

export const useAnalyticsStore = defineStore('analytics', {
  state: () => ({
    source: 'none' as string,
    queryParams: {} as any,
    from: 'none' as string,
    sessionType: 'none' as string
  }),
  actions: {
    setSource(source: string) {
      this.source = source
    },
    setFrom(from: string) {
      this.from = from
    },
    setSessionType(sessionType: string) {
      this.sessionType = sessionType
    },
    setQueryParams(queryParams: any) {
      this.queryParams = queryParams
    },
    resetQueryParams() {
      this.queryParams = {}
    }
  }
})

export const useInitStore = defineStore('initialize', {
  state: () =>
    useStorage(
      INIT_STORE,
      {
        loading: false as boolean,
        categories: [] as Array<any>,
        subcategories: [] as Array<any>,
        objectives: [] as Array<any>,
        games: [] as Array<any>,
        faqs: [] as Array<any>,
        recommendedSessions: [] as Array<any>,
        onboardingSkiped: false as boolean,
        locale: '' as string,
        // Expiration 1 day
        expirationDate: new Date().setDate(new Date().getDate() + 1) as Number
      },
      localStorage,
      { mergeDefaults: true }
    ),
  actions: {
    reset() {
      this.loading = false
      this.categories = []
      this.subcategories = []
      this.objectives = []
      this.games = []
      this.faqs = []
      this.recommendedSessions = []
      this.onboardingSkiped = false
      this.expirationDate = new Date().getDate()
    },
    skipOnboarding() {
      this.onboardingSkiped = true
    },
    setLocale(newLocale: string) {
      this.locale = newLocale
    },
    resetSkipOnboarding() {
      this.onboardingSkiped = false
    },
    async initialize() {
      const appStore = useAppGlobalStore()
      this.loading = true
      try {
        const now: Number = new Date().getDate()
        // if (this.expirationDate > now) return

        const result = await gamesService.getGames()
        // console.log('Games')
        // console.log(result)
        this.categories = []
        this.subcategories = []
        this.objectives = []
        this.games = []
        this.faqs = []
        this.recommendedSessions = []
        const categoriesIds: number[] = []
        const subcategoriesIds: any[] = []
        // const subcategories: any[] = []
        const objectivesIds: any[] = []
        let objetivesIndex: number = 0
        result.forEach((game: any) => {
          this.games.push({
            id: game.id,
            title: te(`games_names.${game.name}`) ? t(`games_names.${game.name}`) : game.name,
            name: game.name,
            description: te(`games_desc.${game.name}`)
              ? t(`games_desc.${game.name}`)
              : game.description,
            schoolsObjectives: game.schoolsObjectives,
            sortPoints: game.sortPoints
          })
          game.schoolsObjectives.forEach((schoolsObjective: any) => {
            if (!objectivesIds.includes(schoolsObjective.id)) {
              // this.objectives.push(schoolsObjective)
              objetivesIndex++
              this.objectives.push({
                index: objetivesIndex,
                id: schoolsObjective.id,
                name: te(`objectives.${schoolsObjective.name}`)
                  ? t(`objectives.${schoolsObjective.name}`)
                  : schoolsObjective.name,
                original_name: schoolsObjective.name,
                subcategory: schoolsObjective.schoolsSubcategory,
                category: schoolsObjective.schoolsSubcategory.category
              })
              objectivesIds.push(schoolsObjective.id)
              if (!subcategoriesIds.includes(schoolsObjective.schoolsSubcategory.id)) {
                // this.subcategories.push(schoolsObjective.schoolsSubcategory)
                this.subcategories.push({
                  index: game.id,
                  id: schoolsObjective.schoolsSubcategory.id,
                  name: te(`subcategories.${schoolsObjective.schoolsSubcategory.name}`)
                    ? t(`subcategories.${schoolsObjective.schoolsSubcategory.name}`)
                    : schoolsObjective.schoolsSubcategory.name,
                  original_name: schoolsObjective.schoolsSubcategory.name,
                  category: {
                    ...schoolsObjective.schoolsSubcategory.category,
                    name: te(`categories.${schoolsObjective.schoolsSubcategory.category.name}`)
                      ? t(`categories.${schoolsObjective.schoolsSubcategory.category.name}`)
                      : schoolsObjective.schoolsSubcategory.category.name,
                    original_name: schoolsObjective.schoolsSubcategory.category.name
                  }
                })
                subcategoriesIds.push(schoolsObjective.schoolsSubcategory.id)
              }
              if (!categoriesIds.includes(schoolsObjective.schoolsSubcategory.category.id)) {
                // this.categories.push(schoolsObjective.schoolsSubcategory.category)
                this.categories.push({
                  id: schoolsObjective.schoolsSubcategory.category.id,
                  name: te(`categories.${schoolsObjective.schoolsSubcategory.category.name}`)
                    ? t(`categories.${schoolsObjective.schoolsSubcategory.category.name}`)
                    : schoolsObjective.schoolsSubcategory.category.name,
                  original_name: schoolsObjective.schoolsSubcategory.category.name
                })
                categoriesIds.push(schoolsObjective.schoolsSubcategory.category.id)
              }
            }
          })
        })
        this.faqs = await faqsService.getFaqs()
        this.recommendedSessions = await recommendedSessionsService.getRecommendedSessions()
        // Sets expiration date to 1 day
        this.expirationDate = new Date().setDate(new Date().getDate() + 1)
        this.loading = false
      } catch (error) {
        console.error('Get Games failed:', error)
        this.loading = false
        appStore.setSnackbarText(t('snackbar.initDataError'))
        throw error
      }
    }
  }
})

export const useLoginStore = defineStore('login', {
  state: () =>
    useStorage(
      LOGIN_STORE,
      {
        email: '' as string,
        password: '' as string,
        repeatPassword: '' as string,
        customToken: '' as string,
        loading: false as boolean,
        userType: undefined as string | undefined,
        userTypeComments: undefined as string | undefined,
        userTypes: [] as string[] | undefined,
        token: '' as string,
        refreshToken: '' as string,
        authType: 'email' as string,
        firebaseUid: undefined as string | undefined,
        isEmailValid: false as boolean,
        isPasswordValid: false as boolean,
        isUserTypeValid: false as boolean,
        loadingRecovery: false as boolean,
        isRecoverEmailSent: false as boolean,
        isLogin: false as boolean
      },
      localStorage,
      { mergeDefaults: true }
    ),
  getters: {
    isSubmitDisabled(): boolean {
      return /* !this.isEmailValid || !this.isPasswordValid */ false
    },
    isSubmitRegisterDisabled(): boolean {
      return /* !this.isEmailValid || !this.isPasswordValid || !this.userType */ false
    }
  },
  actions: {
    resetForm() {
      this.email = ''
      this.password = ''
      this.repeatPassword = ''
      this.authType = 'email'
      this.customToken = ''
      this.firebaseUid = undefined
      this.userType = undefined
      this.userTypeComments = undefined
      this.userTypes = undefined
      this.loading = false
      this.isEmailValid = false
      this.isPasswordValid = false
      this.isUserTypeValid = false
      this.loadingRecovery = false
      this.isRecoverEmailSent = false
    },
    resetStore() {
      this.email = ''
      this.password = ''
      this.repeatPassword = ''
      this.loading = false
      this.authType = 'email'
      this.customToken = ''
      this.firebaseUid = undefined
      this.userType = undefined
      this.userTypeComments = undefined
      this.userTypes = undefined
      this.token = ''
      this.isEmailValid = false
      this.isPasswordValid = false
      this.isUserTypeValid = false
      this.loadingRecovery = false
      this.isRecoverEmailSent = false
    },
    async signupSilent() {
      this.loading = true
      try {
        await authenticateService.signup({
          email: this.email,
          userType: 'TEACHER',
          userTypes: 'TEACHER',
          locale: locale.value === 'es' ? 'es_ES' : 'en_US',
          userTypeComments: null,
          firebaseUid: this.firebaseUid
        })
        this.loading = false
      } catch (error) {
        this.loading = false
        console.error('Silent signup failed:', error)
        throw error
      }
    },
    async signupForm(isSSO: boolean = false) {
      const appStore = useAppGlobalStore()
      const userStore = useUserStore()
      const initStore = useInitStore()
      const gamesStore = useGamesStore()
      this.loading = true
      try {
        // UserType now is a multiple selector, so we need to feed usertype from usertypes array
        this.userType =
          this.userTypes != undefined && this.userTypes != null && this.userTypes.length > 0
            ? this.userTypes[0]
            : undefined
        const result = await authenticateService.signup({
          email: this.email.trim(),
          password: this.password,
          userType: this.userType,
          userTypeComments: this.userTypeComments,
          userTypes: this.userTypes?.join(','),
          locale: locale.value === 'es' ? 'es_ES' : 'en_US',
          firebaseUid:
            this.firebaseUid === null || this.firebaseUid === undefined ? null : this.firebaseUid
        })

        initStore.resetSkipOnboarding()

        const user = result
        userStore.setUser(user)

        setUser({ id: user.id, email: user.email })
        setTags({
          userId: user.id,
          userEmail: user.email,
          userLocale: user.locale,
          userType: user.type,
          userTypeComments: user.userTypeComments,
          userTypes: user.types?.join(','),
          appVersion: user.version
        })
        setUserID(user)
        setUserProps(user, user.children[0])

        userStore.setChildren(user.children.sort(compareById))
        userStore.setChild(user.children[0])

        /* if (!isSSO) {
          const { token, refreshToken, authType } = await login(this.email, this.password)

          this.token = token
          this.refreshToken = refreshToken
          this.authType = authType
        } */

        console.log('Signup Successful!')
        sendEvent('signup', { email: this.email })
        // this.resetForm()
        this.loading = false
      } catch (error: any) {
        console.error('Form signup submission failed:', error)
        const userAlreadyExists = error.message != null && error.message.includes('400')
        const message = userAlreadyExists
          ? t('snackbar.signupExistingUserLogin')
          : t('snackbar.signupCreateUserError')
        appStore.setSnackbarText(message)
        sendEvent('register_error_viewed', {
          error_type: userAlreadyExists ? 'existing_user' : 'general_error'
        })
        this.loading = false
        // Handle the error as needed
        throw error // Re-throw the error for the calling code to handle
      }
    },
    async loginForm() {
      const appStore = useAppGlobalStore()
      this.loading = true
      try {
        // const { token, refreshToken, authType } = await login(this.email, this.password)
        const { token, email, firebaseUid, refreshToken } = await signInWithEmail(
          this.email.trim(),
          this.password
        )

        this.token = token
        this.refreshToken = refreshToken
        this.authType = 'email'
        // Add your actual form submission logic here
        console.log('Login Successful!')
        sendEvent('login', { email: this.email })
        sendEvent('logged_in', { account_type: 'email' })
        // You can also reset the form data after submission
        // this.resetForm()
        this.loading = false
      } catch (error) {
        console.error('Form login submission failed:', error)
        appStore.setSnackbarText(t('snackbar.loginInvalidCredentials'))
        this.loading = false
        // Handle the error as needed
        throw error // Re-throw the error for the calling code to handle
      }
    },
    async signInWithGoogle() {
      const appStore = useAppGlobalStore()
      try {
        const { token, email, firebaseUid } = await signInWithGoogle()
        this.token = token
        this.email = email
        this.authType = 'google'
        this.firebaseUid = firebaseUid
      } catch (error: any) {
        console.log(error.message)
        if (!error.message.includes('popup-closed-by-user'))
          appStore.setSnackbarText(t('snackbar.googleSignInError'))
        throw error
      }
    },
    async signInWithApple() {
      const appStore = useAppGlobalStore()
      try {
        const { token, email, firebaseUid } = await signInWithApple()
        this.token = token
        this.email = email
        this.authType = 'apple'
        this.firebaseUid = firebaseUid
      } catch (error: any) {
        appStore.setSnackbarText(t('snackbar.appleSignInError'))
        throw error
      }
    },
    async signUpWithEmail() {
      const appStore = useAppGlobalStore()
      try {
        const { token, email, firebaseUid } = await signUpWithEmail(
          this.email.trim(),
          this.password
        )
        this.token = token
        this.email = email
        this.authType = 'email'
        this.firebaseUid = firebaseUid
      } catch (error: any) {
        if (error.message.includes('auth/email-already-in-use'))
          appStore.setSnackbarText(t('snackbar.emailUserAlreadyExists'))
        else appStore.setSnackbarText(t('snackbar.emailSignUpError'))
        throw error
      }
    },
    async signInWithCustomToken() {
      const appStore = useAppGlobalStore()
      try {
        const { token, email, firebaseUid } = await signInWithCustomTokenInternal(this.customToken)
        this.token = token
        this.email = email
        this.authType = 'custom'
        this.firebaseUid = firebaseUid
      } catch (error) {
        console.error('Custom Token Login failed:', error)
        appStore.setSnackbarText(t('snackbar.customTokenSignInError'))
        throw error
      }
    },
    async sendRecoveryEmail() {
      const appStore = useAppGlobalStore()
      this.loadingRecovery = true
      try {
        await authenticateService.recoverPassword({
          email: this.email.trim()
        })

        this.isRecoverEmailSent = true
        this.loadingRecovery = false
        console.log('Recover Password Email Sent Successful!')
        appStore.setSnackbarText(t('snackbar.recoverPasswordEmailSent'))
      } catch (error) {
        console.error('Recover Password Email Sent failed:', error)
        appStore.setSnackbarText(t('snackbar.recoverPasswordEmailError'))
        this.loadingRecovery = false
        throw error
      }
    },
    async getCustomToken() {
      try {
        const result = await authenticateService.getCustomToken()
        return result
      } catch (error) {
        console.error('Get custom token failed:', error)
      }
    },
    setToken(token: string) {
      this.token = token
    },
    setRefreshToken(refreshToken: string) {
      this.refreshToken = refreshToken
    },
    setCustomToken(customToken: string) {
      this.customToken = customToken
    }
  }
})

export const useUserStore = defineStore('user', {
  state: () =>
    useStorage(
      USER_STORE,
      {
        loading: false as boolean,
        loadingChildUpdate: false as boolean,
        user: null as any,
        children: [] as Array<any>,
        childSelected: null as any,
        // Expiration 1 day
        expirationDate: new Date().setDate(new Date().getDate() + 1) as Number
      },
      localStorage,
      { mergeDefaults: true }
    ),
  actions: {
    resetStore() {
      this.loading = false
      this.loadingChildUpdate = false
      this.children = []
      this.childSelected = null
      this.expirationDate = new Date().getDate()
    },
    resetChildSelected() {
      this.childSelected = null
    },
    async getUser() {
      const now: Number = new Date().getDate()
      // if (this.expirationDate > now) return

      const appStore = useAppGlobalStore()
      this.loading = true
      try {
        const result = await userService.getUser()
        // console.log('User')
        // console.log(result)
        const user = result
        this.user = user
        this.children = user.children.sort(compareById)
        this.loading = false
        setUser({ id: user.id, email: user.email })
        setTags({
          userId: user.id,
          userEmail: user.email,
          userLocale: user.locale,
          userType: user.type,
          userTypeComments: user.userTypeComments,
          userTypes: user.types?.join(','),
          appVersion: user.version
        })
        setUserID(user)
        setUserProps(user, user.children[0])

        // Sets expiration date to 1 day
        this.expirationDate = new Date().setDate(new Date().getDate() + 1)
      } catch (error) {
        console.error('Get User failed:', error)
        this.loading = false
        throw error
      }
    },
    async setUserCenter(
      name: string | null,
      centerType: string | null,
      phoneNumber: string | null
    ) {
      try {
        const userUpdated = await userService.setUserCenter({ name, centerType, phoneNumber })
        this.user = userUpdated
      } catch (error) {
        console.error('Set User Center failed:', error)
        throw error
      }
    },
    setUser(user: any) {
      this.user = user
    },
    setChildren(children: any[]) {
      this.children = children
      this.user.children = children
    },
    setChild(item: any) {
      this.childSelected = item
      setTags({
        childId: item.id,
        childName: item.name,
        childNeurodiversity: item.neurodiversity == null ? null : item.neurodiversity.join(','),
        childBirth: item.birth,
        childLevel: item.level,
        childPill: item.pill
      })
      setUserProps(this.user, item)
    },
    async updateChild(child: any) {
      const childId = this.childSelected.id
      const costumeId = this.childSelected.costumeId
      const vehicleId = this.childSelected.vehicleId
      const openingPlayed = true
      this.loadingChildUpdate = true
      try {
        const childUpdated = await childService.updateChild(childId, {
          ...child,
          costumeId,
          vehicleId,
          openingPlayed
        })
        this.setChild(childUpdated)
        const newChildren: any[] = this.children.filter((it: any) => it.id !== childUpdated.id)
        newChildren.push(childUpdated)
        this.setChildren(newChildren)
      } catch (error: any) {
        console.error('Update Child error:', error)
        throw error
      }
      this.loadingChildUpdate = false
    },
    async updateUserSettings(emailNotification: boolean) {
      try {
        await userService.updateUserSettings({
          locale: this.user.locale,
          backgroundSound: this.user.backgroundSound,
          pushNotification: this.user.pushNotification,
          emailNotification
        })
        this.user.emailNotification = emailNotification
      } catch (error: any) {
        console.error('Update User Settings error:', error)
        throw error
      }
    },
    async createChild(newChild: any) {
      this.loadingChildUpdate = true
      try {
        const child = await childService.createChild(newChild)
        this.setChild(child)
        const newChildren: any[] = this.children
        newChildren.push(child)
        this.setChildren(newChildren)
      } catch (error: any) {
        console.error('Create Child error:', error)
        throw error
      }
      this.loadingChildUpdate = false
    },
    hasAnyChildrenPlayed() {
      return this.children.some((child: any) => {
        return child.sessions.some((session: any) => {
          return session.timesPlayed > 0
        })
      })
    },
    hasAnySessionCreated() {
      return this.children.some((child: any) => {
        return child.sessions.length > 0
      })
    },
    isSubscribed() {
      if (!this.user || !this.user.subscription) return false
      /* const subscriptionType = this.user.subscription.type ?? 'APP'
      if (subscriptionType !== 'SCHOOLS') return false */
      const dateExpired = this.user.subscription.dateExpiry
      return new Date() < new Date(dateExpired)
    },
    isSchoolsSubscribed() {
      if (!this.user || !this.user.subscription) return false
      const subscriptionType = this.user.subscription.type ?? 'APP'
      const platform = this.user.subscription.platform ?? 'manual'
      const dateExpired = this.user.subscription.dateExpiry
      return (
        subscriptionType === 'SCHOOLS' &&
        new Date() < new Date(dateExpired) &&
        platform !== 'manual'
      )
    },
    isSchoolsEndFreeTier() {
      if (!this.user || !this.user.subscription) return true
      const subscriptionType = this.user.subscription.type ?? 'APP'
      const platform = this.user.subscription.platform ?? 'manual'
      const dateExpired = this.user.subscription.dateExpiry
      return (
        subscriptionType === 'SCHOOLS' &&
        new Date() >= new Date(dateExpired) &&
        platform === 'manual'
      )
    },
    isMaxChildren() {
      if (!this.isSubscribed()) return true
      const subscriptionType = this.user.subscription.type ?? 'APP'
      if (subscriptionType === 'APP') return false
      const maxChildren = this.user.subscription.maxChildren ?? 3
      return this.user.children.length >= maxChildren
    },
    async isMaxSessions() {
      if (this.isSubscribed()) return false
      const sessionPromises = this.user.children.map(async (child: any) => {
        return await gamesScheduleService.getGamesSchedule(child.id)
      })
      const sessionResults = await Promise.all(sessionPromises)
      // Agrupar sesiones por mes
      const groupedByMonth = sessionResults.flat().reduce((acc: any, session: any) => {
        const date = new Date(session.dateCreated)
        const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}` // Año-Mes
        if (!acc[monthKey]) {
          acc[monthKey] = []
        }
        acc[monthKey].push(session)
        return acc
      }, {})

      // Obtener el mes actual
      const currentDate = new Date()
      const currentMonthKey = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}`

      // Verificar si hay 3 o más sesiones en el mes actual
      const currentMonthSessions = groupedByMonth[currentMonthKey] || []
      return currentMonthSessions.length >= 3
    },
    isTestSubscription() {
      if (!this.isSubscribed()) return false
      return (
        this.user.subscription &&
        (this.user.subscription.type === 'APP' ||
          (this.user.subscription.type === 'SCHOOLS' &&
            this.user.subscription.platform === 'manual'))
      )
    }
  }
})

export const useCategoriesStore = defineStore('categories', {
  state: () => ({
    loading: false as boolean,
    categories: [] as Array<any>,
    selectedCategories: [] as Array<any>
  }),
  actions: {
    resetSelectedCategories() {
      this.selectedCategories = []
    },
    resetStore() {
      this.loading = false
      this.categories = []
      this.selectedCategories = []
    },
    getCategories() {
      /* this.loading = true
      this.loading = false */
      const initStore = useInitStore()
      console.log('Get Categories')
      // FIX: Added emotions at start of the list
      const emotionCategory = initStore.categories.find((item) => item.id == 11)
      const otherCategories = initStore.categories.filter((item) => item.id != 11)
      this.categories = otherCategories
      this.categories.unshift(emotionCategory)
    }
  }
})

export const useSubcategoriesStore = defineStore('subcategories', {
  state: () => ({
    loading: false as boolean,
    subcategories: [] as Array<any>,
    selectedSubcategories: [] as Array<any>
  }),
  actions: {
    resetSelectedSubcategories() {
      this.selectedSubcategories = []
    },
    resetStore() {
      this.loading = false
      this.subcategories = []
      this.selectedSubcategories = []
    },
    getSubcategories() {
      const initStore = useInitStore()
      const categoriesStore = useCategoriesStore()
      const categoriesIds = categoriesStore.selectedCategories.map((it: any) => it.id)
      console.log('Get Subcategories')
      this.subcategories = initStore.subcategories.filter((it: any) =>
        categoriesIds.includes(it.category.id)
      )
    }
  }
})

export const useObjectivesStore = defineStore('objectives', {
  state: () => ({
    loading: false as boolean,
    objectives: [] as Array<any>,
    selectedObjectives: [] as Array<any>
  }),
  actions: {
    resetSelectedObjectives() {
      this.selectedObjectives = []
    },
    resetStore() {
      this.loading = false
      this.objectives = []
      this.selectedObjectives = []
    },
    getObjectives() {
      const initStore = useInitStore()
      console.log('Get Objectives')
      this.objectives = initStore.objectives
    },
    setObjectivesSelected(value: any[]) {
      this.selectedObjectives = value
    }
  }
})

export const useGamesStore = defineStore('games', {
  state: () => ({
    loading: false as boolean,
    games: [] as Array<any>,
    selectedGames: [] as Array<any>,
    sessions: [] as Array<any>,
    allSessions: [] as Array<any>,
    loadingSend: false as boolean,
    loadingSessions: false as boolean,
    gamesToSend: [] as Array<any>,
    session: null as any
  }),
  actions: {
    resetStore() {
      this.loading = false
      this.games = []
      this.selectedGames = []
      this.loadingSend = false
      this.loadingSessions = false
      this.gamesToSend = []
      this.sessions = []
      this.allSessions = []
      this.session = null
    },
    resetSelectedGames() {
      this.selectedGames = []
    },
    resetGamesToSend() {
      this.gamesToSend = []
    },
    resetSession() {
      this.resetSelectedGames()
      this.resetGamesToSend()
      const objectivesStore = useObjectivesStore()
      objectivesStore.resetSelectedObjectives()
    },
    getGames() {
      // this.loading = true
      const initStore = useInitStore()
      const objectivesStore = useObjectivesStore()
      const objectivesIds = objectivesStore.selectedObjectives.map((it: any) => it.id)
      console.log('Get Games')
      console.log(objectivesIds)
      const gamesAux = initStore.games.map((it: any) => {
        const objectivesFound = it.schoolsObjectives.filter((it: any) =>
          objectivesIds.includes(it.id)
        )
        if (objectivesFound.length > 0)
          return {
            ...it,
            objectivePrincipal:
              it.schoolsObjectives.find((it: any) => it.principal) ?? objectivesFound[0]
          }
        else null
        /* const objectivesIdsAux = it.schoolsObjectives.map((it: any) => it.id)
        return objectivesIdsAux.some((schoolsObjectiveId: any) =>
          objectivesIds.includes(schoolsObjectiveId)
        ) */
      })
      this.games = gamesAux.filter((it: any) => !!it).sort((a, b) => b.sortPoints - a.sortPoints)
      // this.loading = false
    },
    async getGamesSchedule() {
      const appStore = useAppGlobalStore()
      const userStore = useUserStore()
      this.loadingSessions = true
      try {
        const sessions = await gamesScheduleService.getGamesSchedule(userStore.childSelected.id)
        this.sessions = sessions
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .reverse()
        this.loadingSessions = false
      } catch (error) {
        console.error('Get Games Schedules Sessions failed:', error)
        this.loadingSessions = false
        appStore.setSnackbarText(t('snackbar.gamesScheduledSessionsError'))
        throw error
      }
    },
    async getAllGamesSchedule() {
      const appStore = useAppGlobalStore()
      const userStore = useUserStore()
      this.loadingSessions = true
      try {
        const sessionPromises = userStore.user.children.map(async (child: any) => {
          return await gamesScheduleService.getGamesSchedule(child.id)
        })
        const sessionResults = await Promise.all(sessionPromises)

        this.allSessions = sessionResults
        this.loadingSessions = false
      } catch (error) {
        console.error('Get All Games Schedules Sessions failed:', error)
        this.loadingSessions = false
        appStore.setSnackbarText(t('snackbar.allGamesScheduledSessionsError'))
        throw error
      }
    },
    async sendGamesSchedule() {
      const appStore = useAppGlobalStore()
      const userStore = useUserStore()
      this.loadingSend = true
      try {
        const session = await gamesScheduleService.sendGamesSchedule({
          childId: userStore.childSelected.id,
          gamesIds: this.gamesToSend.map((it: any) => it.id).reverse(),
          dateScheduled: formatISODateWithoutMilliseconds(new Date())
        })
        userStore.childSelected.sessions.push(session)
        userStore.user.children.forEach((child: any) => {
          if (child.id === userStore.childSelected.id) {
            child.sessions.push(session)
          }
        })
        this.loadingSend = false
      } catch (error) {
        console.error('Send Games failed:', error)
        this.loadingSend = false
        appStore.setSnackbarText(t('snackbar.sendGamesError'))
        throw error
      }
    },
    async updateGamesSchedule(sessionId: number, name: string) {
      const userStore = useUserStore()
      try {
        await gamesScheduleService.updateGamesSchedule(sessionId, {
          childId: userStore.childSelected.id,
          name
        })
      } catch (error) {
        console.error('Update Games failed:', error)
      }
    },
    addSelectedGame(item: any) {
      this.selectedGames.push(item)
    },
    removeSelectedGame(item: any) {
      this.selectedGames = this.selectedGames.filter((it) => it.id !== item.id)
    },
    addSelectedGamesToSend() {
      this.gamesToSend = removeDuplicatesById(this.gamesToSend.concat(this.selectedGames))
    },
    addGameToSend(item: any) {
      this.gamesToSend.push(item)
    },
    removeGameToSend(item: any) {
      this.gamesToSend = this.gamesToSend.filter((it) => it.id !== item.id)
    }
  }
})

const capitalizeWords = (dirtySentence: String) => {
  const sentence = dirtySentence.replaceAll('_', ' ')
  // Split the sentence into an array of words
  const words = sentence.split(' ')

  // Capitalize the first letter of each word
  const capitalizedWords = words.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1)
  })

  // Join the words back together into a sentence
  const capitalizedSentence = capitalizedWords.join(' ')

  return capitalizedSentence
}

const removeDuplicatesById = (array: any[]) => {
  const uniqueIds: any = {}
  return array.filter((obj: any) => {
    if (!uniqueIds[obj.id]) {
      uniqueIds[obj.id] = true
      return true
    }
    return false
  })
}

const removeDuplicateObjects = (array: any[]) => {
  const uniqueObjectsSet = new Set()
  return array.filter((obj) => {
    const objString = JSON.stringify(obj)
    const isUnique = !uniqueObjectsSet.has(objString)
    if (isUnique) uniqueObjectsSet.add(objString)
    return isUnique
  })
}

const formatISODateWithoutMilliseconds = (date: Date) => {
  const isoString = date.toISOString()
  return isoString.split('.')[0] + 'Z'
}

const compareById = (a: any, b: any) => {
  return a.id - b.id
}

const m = () => Math.floor(Math.random() * 100) + 1
const testABTel = () => m() < 50
const isUserTypeProfesional = () => {
  const userStore = useUserStore()
  return userStore.user.type === 'PSYCHOLOGIST' || userStore.user.type === 'TEACHER'
}
export const isUserTypeProfesionalAndTestAB = () => isUserTypeProfesional() && testABTel()

export const isNonOnboarding = () => {
  const userStore = useUserStore()
  const initStore = useInitStore()
  if (initStore.onboardingSkiped) return true
  if (userStore.children.length > 1) return true
  return userStore.children.some((child: any) =>
    child.sessions.some((session: any) => session.timesPlayed > 0)
  )
}

const isSubcategoryInArray = (array: any[], object: any) => {
  return array.some((obj) => {
    return obj.categoryId === object.categoryId && obj.subcategoryId === object.subcategoryId
  })
}

const isObjectiveInArray = (array: any[], object: any) => {
  return array.some((obj) => {
    return (
      obj.categoryId === object.categoryId &&
      obj.subcategoryId === object.subcategoryId &&
      obj.objectiveId === object.objectiveId
    )
  })
}

const login = async (email: string, password: string) => {
  const result = await authenticateService.login({ email, password })

  return { token: result.token, refreshToken: result.refreshToken, authType: 'email' }
}
